import { useEffect, useMemo, useState } from "react";
import { InfoHint } from "../components/InfoHint";
import { api } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CalendarDays, ChevronLeft, ChevronRight, Umbrella, Repeat } from "lucide-react";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const GIORNI = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const PRIO_DOT = {
  Bassa: "bg-[#64748B]",
  Media: "bg-[#D97706]",
  Alta: "bg-[#EA580C]",
  Critica: "bg-[#DC2626]",
};
const PRIO_CHIP = {
  Bassa: "bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]",
  Media: "bg-[#FEF3C7] text-[#B45309] border-[#FDE68A]",
  Alta: "bg-[#FFEDD5] text-[#C2410C] border-[#FDBA74]",
  Critica: "bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]",
};

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function Scadenze() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));

  useEffect(() => {
    api.get("/scadenze").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  // Raggruppa per data (YYYY-MM-DD locale della scadenza)
  const byDay = useMemo(() => {
    const map = {};
    for (const it of items) {
      const d = new Date(it.data_scadenza);
      const key = ymd(d);
      (map[key] = map[key] || []).push(it);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => ["Critica", "Alta", "Media", "Bassa"].indexOf(a.priorita) - ["Critica", "Alta", "Media", "Bassa"].indexOf(b.priorita));
    }
    return map;
  }, [items]);

  const openEvent = (ev) => {
    if (ev.type === "polizza") navigate("/condomini");
    else if (ev.type === "ras") navigate("/ras");
    else navigate(`/segnalazioni/${ev.id}`);
  };

  // Costruzione griglia mese (lunedì come primo giorno)
  const cells = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const startOffset = (first.getDay() + 6) % 7; // 0=Lun
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < startOffset; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(year, month, d));
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [cursor]);

  const move = (delta) => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + delta, 1));
  const goToday = () => setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
  const todayKey = ymd(today);

  const totMese = cells.filter(Boolean).reduce((acc, d) => acc + (byDay[ymd(d)]?.length || 0), 0);

  return (
    <div className="max-w-6xl" data-testid="scadenze-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-3">
            <CalendarDays className="w-9 h-9 text-brand" /> Calendario scadenze
          </h1>
          <InfoHint section="scadenze" />
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="cal-prev" variant="outline" size="icon" className="h-11 w-11" onClick={() => move(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div data-testid="cal-month" className="min-w-[200px] text-center font-display text-xl font-bold">
            {MESI[cursor.getMonth()]} {cursor.getFullYear()}
          </div>
          <Button data-testid="cal-next" variant="outline" size="icon" className="h-11 w-11" onClick={() => move(1)}>
            <ChevronRight className="w-5 h-5" />
          </Button>
          <Button data-testid="cal-today" variant="outline" className="h-11 px-4 font-bold" onClick={goToday}>Oggi</Button>
        </div>
      </div>

      {/* Legenda */}
      <div className="flex flex-wrap items-center gap-4 mb-4 text-sm font-semibold text-slate-600">
        <span className="text-slate-500">Priorità:</span>
        {["Critica", "Alta", "Media", "Bassa"].map((p) => (
          <span key={p} className="flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${PRIO_DOT[p]}`} /> {p}
          </span>
        ))}
        <span className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-[#7C3AED]" /> <Umbrella className="w-4 h-4" /> Polizza
        </span>
        <span className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-[#0D9488]" /> <Repeat className="w-4 h-4" /> Attività programmata
        </span>
        <span className="ml-auto text-slate-500">{totMese} scadenze nel mese</span>
      </div>

      <Card className="p-3 sm:p-4 border-2 overflow-x-auto">
        <div className="grid grid-cols-7 gap-1 min-w-[640px]">
          {GIORNI.map((g) => (
            <div key={g} className="text-center text-sm font-bold uppercase tracking-wide text-slate-500 py-2">{g}</div>
          ))}
          {cells.map((d, i) => {
            if (!d) return <div key={`e${i}`} className="min-h-[96px] rounded-lg bg-slate-50/50" />;
            const key = ymd(d);
            const events = byDay[key] || [];
            const isToday = key === todayKey;
            return (
              <div key={key} data-testid={`cal-day-${key}`}
                className={`min-h-[96px] rounded-lg border p-1.5 ${isToday ? "border-brand bg-[#FBEEF3]/40" : "border-slate-200"}`}>
                <div className={`text-sm font-bold mb-1 ${isToday ? "text-brand" : "text-slate-600"}`}>{d.getDate()}</div>
                <div className="space-y-1">
                  {events.slice(0, 3).map((ev) => (
                    <button key={ev.id + ev.type} data-testid={`cal-event-${ev.type === "polizza" ? "pol-" + ev.id : ev.type === "ras" ? "ras-" + ev.id : ev.numero}`}
                      onClick={() => openEvent(ev)}
                      title={ev.type === "polizza"
                        ? `Rinnovo polizza · ${ev.condominio_nome}${ev.numero_polizza ? " · n° " + ev.numero_polizza : ""}`
                        : ev.type === "ras"
                        ? `${ev.titolo} · ${ev.condominio_nome}${ev.fornitore_nome ? " · " + ev.fornitore_nome : ""}`
                        : `${ev.numero} · ${ev.titolo} · ${ev.condominio_nome}`}
                      className={ev.type === "polizza"
                        ? `w-full text-left truncate text-xs font-semibold px-1.5 py-1 rounded border bg-[#EDE9FE] text-[#5B21B6] border-[#C4B5FD] ${ev.in_ritardo ? "ring-2 ring-red-400" : ""}`
                        : ev.type === "ras"
                        ? `w-full text-left truncate text-xs font-semibold px-1.5 py-1 rounded border bg-[#CCFBF1] text-[#0F766E] border-[#5EEAD4] ${ev.in_ritardo ? "ring-2 ring-red-400" : ""}`
                        : `w-full text-left truncate text-xs font-semibold px-1.5 py-1 rounded border ${PRIO_CHIP[ev.priorita] || PRIO_CHIP.Media} ${ev.in_ritardo ? "ring-2 ring-red-400" : ""}`}>
                      {ev.type === "polizza"
                        ? <><Umbrella className="inline w-3 h-3 mr-1 align-middle" />{ev.condominio_nome}</>
                        : ev.type === "ras"
                        ? <><Repeat className="inline w-3 h-3 mr-1 align-middle" />{ev.titolo.replace("Documento RAS · ", "")}</>
                        : <><span className={`inline-block w-2 h-2 rounded-full mr-1 align-middle ${PRIO_DOT[ev.priorita]}`} />{ev.titolo}</>}
                    </button>
                  ))}
                  {events.length > 3 && (
                    <div className="text-xs font-bold text-slate-500 px-1">+{events.length - 3} altre</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {totMese === 0 && (
        <p className="text-center text-slate-500 text-lg mt-6">Nessuna scadenza in questo mese.</p>
      )}
    </div>
  );
}
